/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Sessions pane. Pixel-pinned to the Figma desktop frames
// (file Y95LG1oFzU2PHIvFuMYTfJ):
//   * 3542:4498  — default state. Blue warning, monitor icons,
//                  no destructive button, no checkboxes.
//   * 3542:5985  — multi-select state. Dark warning, checkboxes
//                  replace monitor icons, "Log out" destructive
//                  button in Other-sessions title, "Select all" row.
//   * 3542:6568  — kebab popover ("Options"): "Select" + "Sign out"
//                  (111×76, fill #15171C, stroke #2F333B, r=12).
//
// State machine:
//   - `selectMode === false` → render default frame (3542:4498).
//   - `selectMode === true`  → render multi-select frame (3542:5985).
//   - Per-row kebab opens the Options popover; clicking "Select"
//     enters multi-select AND ticks that row; clicking "Sign out"
//     revokes that single device.
//   - "Log out" destructive button (multi-select title) revokes the
//     ticked devices in bulk via `onSignOutDevices`.
//
// Color tokens lifted verbatim from Figma (see frame data):
//   * Pane bg / current-session card bg : #111318
//   * Other-session card bg (default)   : #15171C (row 1 only) /
//                                         transparent (rows 2–N)
//   * Other-session card bg (select)    : transparent
//   * Warning bg (default)              : #537EFF (blue)
//   * Warning bg (select)               : #15171C, r=12
//   * Title text                        : #F5F6F8
//   * Body / badge text                 : #B5BAC3
//   * Subtitle (location, datestamp)    : #6B7280
//   * Destructive (sign-out)            : #FF5353
//   * Active dot                        : #3CCB4F
//   * Badge bg                          : #222429 (r=6)
//   * Popover stroke                    : #2F333B (1px)

import {
  Check,
  CheckSquare,
  LogOut,
  Monitor,
  MoreVertical,
  Trash2,
  TriangleAlert,
} from "lucide-react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { SettingsRow } from "../SettingsRow";

export interface SessionsPaneDevice {
  readonly deviceId: string;
  readonly label: string;
  readonly platform: string;
  readonly lastSeenAt: string;
  readonly isCurrent: boolean;
  /**
   * Optional location string ("Melbourne, Australia"). The BE
   * doesn't track this today; when absent the supporting text just
   * shows the relative date.
   */
  readonly location?: string;
}

export interface SessionsPaneProps {
  readonly currentDeviceLabel: string;
  readonly currentDeviceSubtitle: string;
  readonly devices?: ReadonlyArray<SessionsPaneDevice>;
  readonly onSignOutAllDevices: () => Promise<void>;
  readonly onSignOutDevices?: (deviceIds: readonly string[]) => Promise<void>;
  /**
   * Sign out just this device. Wired to the auth-layer's
   * `POST /v1/auth/logout` (single-device path), NOT the
   * E2E `POST /v1/devices/{id}/revoke` which the BE rejects for
   * self-revoke. Used by the current-session kebab's "Sign out"
   * action; falls back to `onSignOutAllDevices` when undefined.
   */
  readonly onSignOutCurrentDevice?: () => Promise<void>;
  /**
   * Zero-residue full local wipe of THIS device ("Erase this device data") — the
   * counterpart to the default preserve-resume sign-out, for a shared/at-risk
   * browser. When provided, the pane renders a destructive "Erase this device"
   * action; omitted → hidden.
   */
  readonly onEraseThisDevice?: () => Promise<void>;
  /**
   * Optional re-fetch callback. When provided, the pane calls it
   * after every successful sign-out so the device list reflects
   * the BE state without requiring the user to close + reopen the
   * modal. If omitted the pane still does optimistic local
   * removal — the row disappears immediately on sign-out.
   */
  readonly onRefreshDevices?: () => Promise<readonly SessionsPaneDevice[]>;
  readonly accountEmail?: string;
}

export function SessionsPane(props: SessionsPaneProps): JSX.Element {
  const {
    currentDeviceLabel,
    currentDeviceSubtitle,
    devices,
    onSignOutAllDevices,
    onSignOutDevices,
    onSignOutCurrentDevice,
    onEraseThisDevice,
    onRefreshDevices,
    accountEmail,
  } = props;

  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Local mirror of the device list. Mirrors the `devices` prop on
  // every parent update AND mutates locally on sign-out so the row
  // disappears immediately — the parent's prop is a snapshot taken
  // at tab-open time and won't otherwise refetch until next mount.
  const [devicesLocal, setDevicesLocal] = useState<
    ReadonlyArray<SessionsPaneDevice> | undefined
  >(devices);
  useEffect(() => {
    setDevicesLocal(devices);
  }, [devices]);

  const removeFromLocal = useCallback((ids: readonly string[]) => {
    const removeSet = new Set(ids);
    setDevicesLocal((prev) =>
      prev ? prev.filter((d) => !removeSet.has(d.deviceId)) : prev,
    );
  }, []);

  const refreshAfterSignOut = useCallback(async () => {
    if (onRefreshDevices === undefined) return;
    try {
      const next = await onRefreshDevices();
      setDevicesLocal(next);
    } catch {
      // Refresh errors are non-fatal — the optimistic removal
      // already updated the list correctly in the common case.
    }
  }, [onRefreshDevices]);

  const current: SessionsPaneDevice | null =
    devicesLocal?.find((d) => d.isCurrent) ?? null;
  const others = useMemo(
    () => (devicesLocal ? devicesLocal.filter((d) => !d.isCurrent) : []),
    [devicesLocal],
  );

  // Drop selections that no longer exist when the device list changes.
  useEffect(() => {
    const valid = new Set(others.map((d) => d.deviceId));
    setSelected((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (valid.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [others]);

  // Exit select mode when there are no more selectable rows.
  useEffect(() => {
    if (selectMode && others.length === 0) {
      setSelectMode(false);
      setSelected(new Set());
    }
  }, [selectMode, others.length]);

  const allSelected = others.length > 0 && selected.size === others.length;

  const toggleOne = useCallback((deviceId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === others.length
        ? new Set()
        : new Set(others.map((d) => d.deviceId)),
    );
  }, [others]);

  const enterSelectModeWith = useCallback((deviceId: string) => {
    setSelectMode(true);
    setSelected(new Set([deviceId]));
    setOpenMenu(null);
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, []);

  const handleSignOutOne = useCallback(
    async (deviceId: string) => {
      setSigningOut(true);
      setError(null);
      setOpenMenu(null);
      try {
        if (onSignOutDevices) {
          await onSignOutDevices([deviceId]);
        } else {
          throw new Error("Per-device sign-out isn't wired on this host.");
        }
        removeFromLocal([deviceId]);
        void refreshAfterSignOut();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setSigningOut(false);
      }
    },
    [onSignOutDevices, removeFromLocal, refreshAfterSignOut],
  );

  const handleLogoutSelected = useCallback(async () => {
    if (selected.size === 0) return;
    setSigningOut(true);
    setError(null);
    try {
      const ids = Array.from(selected);
      if (onSignOutDevices) {
        await onSignOutDevices(ids);
      } else if (allSelected) {
        await onSignOutAllDevices();
      } else {
        throw new Error("Per-device sign-out isn't wired on this host.");
      }
      removeFromLocal(ids);
      exitSelectMode();
      void refreshAfterSignOut();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSigningOut(false);
    }
  }, [
    selected,
    allSelected,
    onSignOutDevices,
    onSignOutAllDevices,
    exitSelectMode,
    removeFromLocal,
    refreshAfterSignOut,
  ]);

  const handleSignOutAll = useCallback(async () => {
    setSigningOut(true);
    setError(null);
    setOpenMenu(null);
    try {
      await onSignOutAllDevices();
      // Clearing every device (including current) — host will
      // typically reload the app right after this returns. We
      // still optimistically empty the list so any flash before
      // the reload shows the right state.
      setDevicesLocal([]);
      exitSelectMode();
      void refreshAfterSignOut();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSigningOut(false);
    }
  }, [onSignOutAllDevices, exitSelectMode, refreshAfterSignOut]);

  // "Sign out" on the current-session kebab (Figma 3542:6568). Falls
  // back to the all-devices revoke when the host hasn't wired a
  // per-device callback that can target the caller's own device.
  //
  // Routing precedence:
  //   1. `onSignOutCurrentDevice` — auth-layer single-device logout
  //      (`POST /v1/auth/logout`). This is the correct path for
  //      "sign out THIS device" because the BE rejects self-revoke
  //      via the E2E `POST /v1/devices/{id}/revoke` endpoint with
  //      409. The host (SettingsModalHost) also runs the full local
  //      wipe + reload.
  //   2. `onSignOutDevices([current.deviceId])` — only used when the
  //      auth-layer callback isn't wired. This path can fail on
  //      self-revoke; kept as a fallback for surfaces that haven't
  //      threaded the dedicated prop yet.
  //   3. `onSignOutAllDevices` — last resort.
  const handleSignOutCurrentOnly = useCallback(async () => {
    setSigningOut(true);
    setError(null);
    setOpenMenu(null);
    try {
      if (onSignOutCurrentDevice) {
        await onSignOutCurrentDevice();
        if (current) removeFromLocal([current.deviceId]);
      } else if (current && onSignOutDevices) {
        await onSignOutDevices([current.deviceId]);
        removeFromLocal([current.deviceId]);
      } else {
        await onSignOutAllDevices();
        setDevicesLocal([]);
      }
      exitSelectMode();
      void refreshAfterSignOut();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSigningOut(false);
    }
  }, [
    current,
    onSignOutCurrentDevice,
    onSignOutDevices,
    onSignOutAllDevices,
    exitSelectMode,
    removeFromLocal,
    refreshAfterSignOut,
  ]);

  const warningBody = accountEmail
    ? `We'll alert you via ${accountEmail} if there is any unusual activity on your account.`
    : "Verify your sessions for enhanced secure messaging or sign out from those you don't recognize or use anymore.";

  return (
    <SettingsRow
      variant="hero"
      title="Security recommendations"
      description="Improve your account security by following these recommendations."
      testID="settings-session-security"
    >
      {/* Warning banner — two variants per Figma. */}
      {selectMode ? <WarningDark /> : <WarningBlue />}

      {/* "Where you're logged in" sub-section.

          z-index trap notice: react-native-web emits a `z-index: 0`
          baseline on every <View> once it has any flex / position
          rule attached. The inner anchor View below sets
          `zIndex: 1000` to lift the popover, BUT that 1000 is
          relative to THIS wrapper's stacking context — and this
          wrapper sits at z-index 0, peer to the sibling sections
          rendered after it (current-session card + "Other
          sessions"). DOM order then puts those siblings on top,
          hiding the popover. The previous `style={{ zIndex:
          undefined }}` was a no-op (React drops undefined). The
          actual fix is to promote THIS wrapper to a numeric
          z-index higher than the siblings whenever the popover is
          open; once closed it falls back to the default. */}
      <View
        testID="settings-session-where"
        className="flex flex-col gap-0.5"
        style={{
          zIndex: openMenu === "__current__" ? 1001 : undefined,
        }}
      >
        {/* Header row also needs an explicit z-index lift when
            the popover is open. Reason: within the wrapper's
            stacking context the header (DOM-first) and the
            warningBody Text below (DOM-second) both sit at the
            default level — react-native-web emits a `z-index: 0`
            baseline so the supporting Text paints on top of the
            popover by DOM order. Lifting the header to a numeric
            z-index inside the wrapper's context makes it win
            against the Text without affecting outer layout. */}
        <View
          className="flex flex-row items-center justify-between"
          style={{
            zIndex: openMenu === "__current__" ? 2 : undefined,
          }}
        >
          <Text className="text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
            Where you're logged in
          </Text>
          {/* Popover is anchored to the kebab itself, not to
					    the session card below. The wrapping View is the
					    positioning context (`position: relative` via
					    NativeWind), and the kebab + popover are
					    siblings inside it — so when the popover renders
					    with `right: 8; top: 32`, those coordinates are
					    measured from the kebab's bounding box, not the
					    card the user *isn't* clicking. */}
          <View
            className="relative"
            style={{
              zIndex: openMenu === "__current__" ? 1000 : undefined,
            }}
          >
            <Pressable
              onPress={() =>
                setOpenMenu(openMenu === "__current__" ? null : "__current__")
              }
              accessibilityLabel="Current session options"
              className="w-5 h-5 items-center justify-center"
            >
              <MoreVertical size={20} strokeWidth={1.75} color="#8C929E" />
            </Pressable>
            {openMenu === "__current__" && (
              <OptionsPopover
                kind="current-row"
                onSignOut={() => void handleSignOutCurrentOnly()}
                onSignOutAll={() => void handleSignOutAll()}
                {...(onEraseThisDevice !== undefined
                  ? {
                      onEraseThisDevice: () => {
                        setOpenMenu(null);
                        void onEraseThisDevice();
                      },
                    }
                  : {})}
                onDismiss={() => setOpenMenu(null)}
              />
            )}
          </View>
        </View>
        <Text className="text-[14px] leading-[20px] font-normal text-light-text-secondary dark:text-[#B5BAC3]">
          {warningBody}
        </Text>
      </View>

      {/* Current session card. Always shows monitor icon (the
			    current device is not selectable). */}
      <View
        testID="settings-session-current"
        className="
					flex flex-row items-center
					px-2 py-2 rounded-lg gap-4
					bg-light-surface-primary dark:bg-[#111318]
				"
      >
        <MonitorIcon />
        <View className="min-w-0 flex-1">
          <TitleAndBadge
            title={
              current && current.label.length > 0
                ? current.label
                : currentDeviceLabel
            }
            badgeText="Active now"
          />
          <SupportingText
            text={current ? describeSession(current) : currentDeviceSubtitle}
          />
        </View>
      </View>

      {/* "Other sessions" sub-section. */}
      {others.length > 0 && (
        <>
          {/* This wrapper holds only the "Other sessions" title +
              warning Text. The per-row kebab popovers live in the
              device-list View AFTER this wrapper in DOM — so
              lifting THIS wrapper above the list would HIDE the
              popovers (the wrapper paints over them). Leave the
              wrapper at default level; the row's own zIndex 1000
              handles popover stacking inside the list. */}
          <View testID="settings-session-other" className="flex flex-col gap-0.5">
            <View className="flex flex-row items-center justify-between">
              <Text className="text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
                Other sessions
              </Text>
              {selectMode && (
                <DestructiveLogOutButton
                  disabled={selected.size === 0 || signingOut}
                  busy={signingOut}
                  onPress={() => void handleLogoutSelected()}
                />
              )}
            </View>
            <Text className="text-[14px] leading-[20px] font-normal text-light-text-secondary dark:text-[#B5BAC3]">
              {warningBody}
            </Text>
          </View>

          {/* Select-all row (multi-select only). */}
          {selectMode && (
            <View className="flex flex-row items-center gap-2 pl-2 pb-2">
              <Checkbox
                checked={allSelected}
                onPress={toggleAll}
                accessibilityLabel="Select all sessions"
              />
              <Text className="text-[14px] leading-[20px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
                Select all
              </Text>
            </View>
          )}

          {/* History — list of session rows. */}
          <View className="flex flex-col gap-2">
            {others.map((device, index) => (
              <View
                key={device.deviceId}
                className="relative"
                // Lift this row's stacking context above its
                // siblings while its popover is open so the
                // popover paints over later rows. Without
                // this, react-native-web's default
                // `z-index: 0` on every View traps the
                // popover inside this row's stacking layer.
                style={{
                  zIndex: openMenu === device.deviceId ? 1000 : undefined,
                }}
              >
                <View
                  className={`
										flex flex-row items-center
										px-2 py-2 rounded-lg gap-4
										${
                      !selectMode && index === 0
                        ? "bg-light-surface-secondary dark:bg-[#15171C]"
                        : selected.has(device.deviceId)
                          ? "bg-light-surface-tertiary dark:bg-[#22242A]"
                          : ""
                    }
									`}
                >
                  {selectMode ? (
                    <Checkbox
                      checked={selected.has(device.deviceId)}
                      onPress={() => toggleOne(device.deviceId)}
                      accessibilityLabel={`Select ${device.label}`}
                    />
                  ) : (
                    <MonitorIcon />
                  )}
                  <View className="min-w-0 flex-1">
                    <View className="flex flex-row items-center gap-2">
                      <View className="flex flex-row items-center gap-2 flex-1 min-w-0">
                        <Text
                          className="text-[14px] leading-[20px] font-bold text-light-text-primary dark:text-[#F5F6F8]"
                          numberOfLines={1}
                        >
                          {device.label.length > 0
                            ? device.label
                            : platformLabel(device.platform)}
                        </Text>
                        <ActiveBadge
                          text={`Active ${formatRelative(device.lastSeenAt)}`}
                        />
                      </View>
                      {/* Per Figma: rows 2-N (in default mode) embed the kebab
											    inline next to the badge; row 1 hosts it after the
											    text frame. In select mode we suppress the kebab —
											    the destructive title button drives the bulk action. */}
                      {!selectMode && index !== 0 && (
                        <Pressable
                          onPress={() =>
                            setOpenMenu(
                              openMenu === device.deviceId
                                ? null
                                : device.deviceId,
                            )
                          }
                          accessibilityLabel="Session options"
                          className="w-5 h-5 items-center justify-center"
                        >
                          <MoreVertical
                            size={20}
                            strokeWidth={1.75}
                            color="#8C929E"
                          />
                        </Pressable>
                      )}
                    </View>
                    <SupportingText text={describeSession(device)} />
                  </View>
                  {/* Row-1 kebab in default mode (Figma 3542:4498 first
									    Other-sessions card has the kebab as a sibling of the
									    text frame, not nested). */}
                  {!selectMode && index === 0 && (
                    <Pressable
                      onPress={() =>
                        setOpenMenu(
                          openMenu === device.deviceId ? null : device.deviceId,
                        )
                      }
                      accessibilityLabel="Session options"
                      className="w-5 h-5 items-center justify-center"
                    >
                      <MoreVertical
                        size={20}
                        strokeWidth={1.75}
                        color="#8C929E"
                      />
                    </Pressable>
                  )}
                </View>
                {openMenu === device.deviceId && (
                  <OptionsPopover
                    kind="other-row"
                    onSelect={() => enterSelectModeWith(device.deviceId)}
                    onSignOut={() => void handleSignOutOne(device.deviceId)}
                    onDismiss={() => setOpenMenu(null)}
                  />
                )}
              </View>
            ))}
          </View>
        </>
      )}

      {error != null && (
        <Text className="text-[12px] leading-[16px] text-[#FF5353] px-2">
          {error}
        </Text>
      )}
    </SettingsRow>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

function MonitorIcon(): JSX.Element {
  return (
    <View className="w-[18px] h-[18px] items-center justify-center">
      <Monitor size={18} strokeWidth={1.2} color="#E8EAED" />
    </View>
  );
}

interface TitleAndBadgeProps {
  readonly title: string;
  readonly badgeText: string;
}

function TitleAndBadge(props: TitleAndBadgeProps): JSX.Element {
  return (
    <View className="flex flex-row items-center gap-2">
      <Text className="text-[14px] leading-[20px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
        {props.title}
      </Text>
      <ActiveBadge text={props.badgeText} />
    </View>
  );
}

function ActiveBadge(props: { readonly text: string }): JSX.Element {
  return (
    <View
      className="
				flex flex-row items-center gap-1
				bg-light-surface-tertiary dark:bg-[#222429]
				rounded-md
			"
      style={{ paddingHorizontal: 6, paddingVertical: 2 }}
    >
      <View className="w-1.5 h-1.5 rounded-full bg-[#3CCB4F]" />
      <Text className="text-[12px] leading-[17px] font-bold text-light-text-secondary dark:text-[#B5BAC3]">
        {props.text}
      </Text>
    </View>
  );
}

function SupportingText(props: { readonly text: string }): JSX.Element {
  return (
    <Text
      className="text-[14px] leading-[20px] font-normal text-light-text-tertiary dark:text-[#6B7280]"
      style={{ marginTop: 4 }}
    >
      {props.text}
    </Text>
  );
}

// ── Warning banners ─────────────────────────────────────────────────

function WarningBlue(): JSX.Element {
  // Figma 3542:4498 — Warning frame 647×53, fill #537EFF, pad 8,
  // gap 8, r=8, HORIZONTAL. Icon 12×12 + title/body stack.
  return (
    <View
      className="
				flex flex-row items-start gap-2
				px-2 py-2 rounded-lg
				bg-[#537EFF]
			"
    >
      <View className="pt-0.5">
        <TriangleAlert size={12} strokeWidth={1.75} color="#FFFFFF" />
      </View>
      <View className="flex-1">
        <Text className="text-[14px] leading-[20px] font-bold text-white">
          Unverified sessions
        </Text>
        <Text className="text-[12px] leading-[17px] font-normal text-white/85 mt-0.5">
          Verify your sessions for enhanced secure messaging or sign out from
          those you don't recognize or use anymore.
        </Text>
      </View>
    </View>
  );
}

function WarningDark(): JSX.Element {
  // Figma 3542:5985 — Warning frame 524×70, fill #15171C, pad 8,
  // r=12, VERTICAL. Title row (text + icon, gap 4) above body text.
  return (
    <View
      className="
				flex flex-col gap-1
				px-2 py-2 rounded-xl
				bg-light-surface-secondary dark:bg-[#15171C]
			"
    >
      <View className="flex flex-row items-center gap-1">
        <Text className="text-[14px] leading-[20px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
          Unverified sessions
        </Text>
        <TriangleAlert size={12} strokeWidth={1.75} color="#B5BAC3" />
      </View>
      <Text className="text-[12px] leading-[17px] font-normal text-light-text-secondary dark:text-[#B5BAC3]">
        Verify your sessions for enhanced secure messaging or sign out from
        those you don't recognize or use anymore.
      </Text>
    </View>
  );
}

// ── DestructiveLogOutButton ─────────────────────────────────────────

interface DestructiveLogOutButtonProps {
  readonly disabled: boolean;
  readonly busy: boolean;
  readonly onPress: () => void;
}

function DestructiveLogOutButton(
  props: DestructiveLogOutButtonProps,
): JSX.Element {
  // Figma 3542:5985 — 85×20, pad left 12, gap 8, r=8.
  // "Log out" 14/19.6 regular #ff5353 + log-out icon 18×18.
  const { disabled, busy, onPress } = props;
  const color = disabled ? "#6B7280" : "#FF5353";
  return (
    <Pressable
      onPress={() => {
        if (disabled || busy) return;
        onPress();
      }}
      disabled={disabled || busy}
      className="flex flex-row items-center gap-2 pl-3 py-1 rounded-lg"
      accessibilityRole="button"
      accessibilityLabel="Log out selected sessions"
    >
      <LogOut size={18} strokeWidth={1.75} color={color} />
      <Text
        className="text-[14px] leading-[20px] font-normal"
        style={{ color }}
      >
        {busy ? "Logging out…" : "Log out"}
      </Text>
    </Pressable>
  );
}

// ── Checkbox ────────────────────────────────────────────────────────
// Figma 3542:5985 — exactly 20×20, borderRadius 6, fill #15171C,
// stroke #2F333B 1px (inside). Checked state uses brand blue #537EFF.
// Uses inline styles (not Tailwind classnames) so dimensions and
// corner radius land pixel-exactly across react-native-web + RN-macOS.

interface CheckboxProps {
  readonly checked: boolean;
  readonly onPress: () => void;
  readonly accessibilityLabel: string;
}

function Checkbox(props: CheckboxProps): JSX.Element {
  const { checked, onPress, accessibilityLabel } = props;
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const bg = checked
    ? pressed
      ? "#3D63E6"
      : hovered
        ? "#5F8AFF"
        : "#537EFF"
    : pressed
      ? "#1A1D24"
      : hovered
        ? "#1B1E25"
        : "#15171C";
  const borderColor = checked ? "#537EFF" : hovered ? "#4B515C" : "#2F333B";

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel={accessibilityLabel}
      style={{
        width: 20,
        height: 20,
        borderRadius: 6,
        borderWidth: 1,
        borderColor,
        backgroundColor: bg,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {checked && <Check size={14} strokeWidth={3} color="#FFFFFF" />}
    </Pressable>
  );
}

// ── OptionsPopover ──────────────────────────────────────────────────
// Two pixel-exact variants per Figma:
//   * `kind="other-row"`     — Figma 3542:4613   111×76, r=12, items
//                              103×34 with 20×20 icons + 16/22.4 bold
//                              labels. Used by "Other sessions" rows.
//                              Menu: [Select, Sign out].
//   * `kind="current-row"`   — Figma 3542:6568   208×80, r=16, items
//                              200×36 (and 100×36) with 24×24 icons +
//                              14/19.6 bold labels. Used by the
//                              current-session kebab.
//                              Menu: [Sign out, Sign out from all devices].
//
// Both have GLASS backdrop (Figma effect) and 1px stroke #2F333B
// inset on a #15171C fill. zIndex 1000 so the popover layers above
// every sibling regardless of stacking context.

interface OtherRowOptionsProps {
  readonly kind: "other-row";
  readonly onSelect: () => void;
  readonly onSignOut: () => void;
  readonly onDismiss: () => void;
  readonly selectDisabled?: boolean;
  readonly signOutDisabled?: boolean;
}

interface CurrentRowOptionsProps {
  readonly kind: "current-row";
  readonly onSignOut: () => void;
  readonly onSignOutAll: () => void;
  /** Zero-residue full local wipe of THIS device. Rendered as a second
   *  destructive item under "Sign out" when provided. */
  readonly onEraseThisDevice?: () => void;
  readonly onDismiss: () => void;
  readonly signOutDisabled?: boolean;
  readonly signOutAllDisabled?: boolean;
}

type OptionsPopoverProps = OtherRowOptionsProps | CurrentRowOptionsProps;

function OptionsPopover(props: OptionsPopoverProps): JSX.Element {
  const ref = useRef<View>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onDocClick = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      const node = ref.current as unknown as HTMLElement | null;
      if (node && target && node.contains(target)) return;
      props.onDismiss();
    };
    const t = setTimeout(() => {
      window.addEventListener("click", onDocClick);
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener("click", onDocClick);
    };
  }, [props]);

  const isOther = props.kind === "other-row";
  const radius = isOther ? 12 : 16;
  const iconSize = isOther ? 20 : 24;
  const labelFontSize = isOther ? 16 : 14;
  const labelLineHeight = isOther ? 22 : 19.6;
  const minWidth = isOther ? 111 : 208;
  const itemHeight = isOther ? 34 : 36;

  return (
    <View
      ref={ref}
      style={{
        position: "absolute",
        right: 8,
        top: 32,
        zIndex: 1000,
        minWidth,
        padding: 4,
        borderRadius: radius,
        borderWidth: 1,
        borderColor: "#2F333B",
        backgroundColor: "rgba(21, 23, 28, 0.92)",
        // GLASS / backdrop blur — Figma effect "GLASS" on both
        // popovers. Defensive CSS shim: react-native-web maps
        // `backdropFilter` through; harmless on macOS.
        ...({
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
        } as object),
      }}
    >
      {isOther ? (
        <>
          <PopoverItem
            icon={
              <CheckSquare size={iconSize} strokeWidth={1.5} color="#E8EAED" />
            }
            label="Select"
            disabled={props.selectDisabled ?? false}
            onPress={props.onSelect}
            labelColor="#F5F6F8"
            fontSize={labelFontSize}
            lineHeight={labelLineHeight}
            iconSize={iconSize}
            itemHeight={itemHeight}
          />
          <PopoverItem
            icon={<LogOut size={iconSize} strokeWidth={1.5} color="#FF5353" />}
            label="Sign out"
            disabled={props.signOutDisabled ?? false}
            onPress={props.onSignOut}
            labelColor="#FF5353"
            fontSize={labelFontSize}
            lineHeight={labelLineHeight}
            iconSize={iconSize}
            itemHeight={itemHeight}
          />
        </>
      ) : (
        <>
          <PopoverItem
            icon={<LogOut size={iconSize} strokeWidth={1.5} color="#FF5353" />}
            label="Sign out"
            disabled={props.signOutDisabled ?? false}
            onPress={props.onSignOut}
            labelColor="#FF5353"
            fontSize={labelFontSize}
            lineHeight={labelLineHeight}
            iconSize={iconSize}
            itemHeight={itemHeight}
          />
          <PopoverItem
            icon={<LogOut size={iconSize} strokeWidth={1.5} color="#FF5353" />}
            label="Sign out from all devices"
            disabled={props.signOutAllDisabled ?? false}
            onPress={props.onSignOutAll}
            labelColor="#FF5353"
            fontSize={labelFontSize}
            lineHeight={labelLineHeight}
            iconSize={iconSize}
            itemHeight={itemHeight}
          />
          {props.onEraseThisDevice !== undefined ? (
            // Zero-residue counterpart to the preserve-resume "Sign out": wipe
            // ALL local data on this device (for a shared/at-risk browser).
            <PopoverItem
              icon={<Trash2 size={iconSize} strokeWidth={1.5} color="#FF5353" />}
              label="Erase this device"
              disabled={false}
              onPress={props.onEraseThisDevice}
              labelColor="#FF5353"
              fontSize={labelFontSize}
              lineHeight={labelLineHeight}
              iconSize={iconSize}
              itemHeight={itemHeight}
            />
          ) : null}
        </>
      )}
    </View>
  );
}

interface PopoverItemProps {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly onPress: () => void;
  readonly disabled: boolean;
  readonly labelColor: string;
  readonly fontSize: number;
  readonly lineHeight: number;
  readonly iconSize: number;
  readonly itemHeight: number;
}

function PopoverItem(props: PopoverItemProps): JSX.Element {
  const {
    icon,
    label,
    onPress,
    disabled,
    labelColor,
    fontSize,
    lineHeight,
    iconSize,
    itemHeight,
  } = props;
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <Pressable
      onPress={() => {
        if (disabled) return;
        onPress();
      }}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      disabled={disabled}
      style={{
        flexDirection: "row",
        alignItems: "center",
        columnGap: 8,
        paddingVertical: 6,
        paddingHorizontal: 8,
        borderRadius: 8,
        minHeight: itemHeight,
        backgroundColor: disabled
          ? "transparent"
          : pressed
            ? "#2F333B"
            : hovered
              ? "#22242A"
              : "transparent",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <View
        style={{
          width: iconSize,
          height: iconSize,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </View>
      <Text
        style={{
          fontSize,
          lineHeight,
          fontWeight: "700",
          color: disabled ? "#6B7280" : labelColor,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

// ── helpers ─────────────────────────────────────────────────────────

function platformLabel(platform: string): string {
  switch (platform) {
    case "ios":
      return "iPhone";
    case "android":
      return "Android";
    case "macos":
      return "Mac";
    case "windows":
      return "Windows PC";
    case "web":
      return "Web browser";
    default:
      return "Device";
  }
}

function describeSession(device: SessionsPaneDevice): string {
  // Figma uses "Melbourne, Australia • 22 Mar at 10:40 AM" — when
  // the BE supplies a location string we honour the format exactly;
  // otherwise fall back to "<Platform> • Last active <relative>".
  const stamp = formatFigmaDate(device.lastSeenAt);
  if (device.location && device.location.length > 0) {
    return `${device.location} • ${stamp}`;
  }
  return `${platformLabel(device.platform)} • Last active ${formatRelative(
    device.lastSeenAt,
  )}`;
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "recently";
  const diffMs = Date.now() - t;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 4) return `${wk}w ago`;
  const months = Math.floor(day / 30);
  if (months < 12) return `${months} months ago`;
  return new Date(t).toLocaleDateString();
}

function formatFigmaDate(iso: string): string {
  // Figma format: "22 Mar at 10:40 AM"
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "recently";
  const date = new Date(t);
  const day = date.getDate();
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const m = months[date.getMonth()] ?? "?";
  let h = date.getHours();
  const min = date.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  const mm = min < 10 ? `0${min}` : `${min}`;
  return `${day} ${m} at ${h}:${mm} ${ampm}`;
}
